import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { defineAbilityFor, type AppPermission } from "@recobra/core";

import { authQueryOptions } from "./authQuery";

export type RouteAccessContext = {
  queryClient: QueryClient;
};

/**
 * First route a signed-in user should land on. A caller with zero recognized
 * roles can reach nothing, so they go to the dedicated no-access screen —
 * never to `/cases` (which is now gated and would bounce them straight back
 * here, looping). Otherwise pick the most capable landing they can actually
 * use: dashboard for internal staff, My Tasks for anyone with task access,
 * and the cases list as the fallback for client/window roles without tasks.
 */
export const defaultAuthedRoute = (
  groups: readonly string[],
): "/" | "/cases" | "/my-tasks" | "/no-access" => {
  const ability = defineAbilityFor({ groups });
  if (ability.can("read", "Dashboard")) return "/";
  if (ability.can("read", "Task")) return "/my-tasks";
  if (ability.can("read", "Case")) return "/cases";

  return "/no-access";
};

const redirectToDefaultAuthedRoute = (groups: readonly string[]) => {
  // TanStack Router requires throwing the redirect return value.
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- redirect() is the router API
  throw redirect({ to: defaultAuthedRoute(groups) });
};

export const ensureAuthGroups = async (queryClient: QueryClient): Promise<readonly string[]> => {
  const auth = await queryClient.ensureQueryData(authQueryOptions);

  return auth.cognitoGroups;
};

export const requireRouteAccess = async (
  queryClient: QueryClient,
  ...permissions: [AppPermission, ...AppPermission[]]
): Promise<readonly string[]> => {
  const groups = await ensureAuthGroups(queryClient);
  const ability = defineAbilityFor({ groups });
  if (permissions.some((permission) => ability.cannot(...permission))) {
    redirectToDefaultAuthedRoute(groups);
  }

  return groups;
};
